import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Scales, Lock, Eye, EyeSlash, CheckCircle, ArrowLeft } from '@phosphor-icons/react';

import { API } from '@/lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <Scales weight="duotone" className="w-12 h-12 text-[#002FA7] mx-auto mb-4" />
          <h1 className="font-outfit text-2xl font-bold text-[#09090B] mb-2">Invalid Reset Link</h1>
          <p className="text-[#6B7280] font-ibm mb-6">No reset token found. Please request a new password reset link.</p>
          <Link to="/forgot-password" className="inline-block px-6 py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Scales weight="duotone" className="w-8 h-8 text-[#002FA7]" />
          <span className="font-outfit text-2xl font-bold text-[#09090B]">ClarityCase AI</span>
        </div>

        {success ? (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={32} className="text-[#34C759]" />
              <h1 className="font-outfit text-2xl font-bold text-[#09090B]">Password Reset</h1>
            </div>
            <p className="text-[#6B7280] font-ibm mb-6" data-testid="reset-success-message">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Link to="/login" data-testid="go-to-login"
              className="block w-full text-center py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all">
              Sign In
            </Link>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lock size={24} className="text-[#002FA7]" />
              <h1 className="font-outfit text-3xl font-bold text-[#09090B]">Set New Password</h1>
            </div>
            <p className="text-[#6B7280] font-ibm mb-8">Enter your new password below.</p>

            {error && <div data-testid="reset-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-ibm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">New Password</label>
                <div className="relative">
                  <input data-testid="reset-password-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all pr-12"
                    placeholder="Min 6 characters" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#09090B]">
                    {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Confirm Password</label>
                <input data-testid="reset-confirm-password-input" type={showPw ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all"
                  placeholder="Confirm your password" />
              </div>
              <button data-testid="reset-submit-button" type="submit" disabled={loading}
                className="w-full py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] active:scale-[0.98] transition-all disabled:opacity-50">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-[#6B7280] font-ibm">
          <Link to="/login" className="flex items-center gap-1 justify-center text-[#002FA7] font-semibold hover:underline" data-testid="back-to-login-link">
            <ArrowLeft size={14} /> Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
